import { ChangeEvent, Fragment } from 'react';
import { MCP_FLAGS, MCP_FLAG_DEPENDENCIES, MCP_FLAG_VALUES } from '@prerender/mcp-flags';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { ExtractHiddenCssLocation, User } from '../../../../services/UsersService';
import UserUpdateButton from '../../UserUpdateButton';
import {
  setCustomRecacheDelay,
  setMobileAdaptive,
  setUserPausedRecaching,
  setIgnoreAllQueryParams,
  setExtractHiddenCss,
  setOnlyServeCacheHits,
  setWhitelistQueryParams,
  setCacheFreshness,
  setExtractHiddenCssLocation,
  setForceApiAccess,
  setSlowRenderTriggerSeconds,
  setAvailableFeatures,
  userState,
} from '../UserSlice';
import UserSettings from './settings/UserSettings';
import { RequireTier } from '../../../auth/RequireTier';
import { useListFeatureFlagsQuery } from '../../../featureFlags/featureFlagsApi';
import { buildFeatureRows } from './grantableFeatures';
import './UserSettingsTab.css';

const MCP_DEPENDENT_FLAGS = MCP_FLAG_VALUES.filter((f) =>
  MCP_FLAG_DEPENDENCIES[f].includes(MCP_FLAGS.MCP_ENABLED),
);

const isToggleDisabled = (key: string, features: readonly string[]): boolean =>
  (MCP_DEPENDENT_FLAGS as readonly string[]).includes(key) &&
  !features.includes(MCP_FLAGS.MCP_ENABLED);

const FEATURE_LIST_LOADING = 'Loading features…';
const FEATURE_LIST_FAILED = 'The feature list could not be loaded.';
const FEATURE_REGISTRY_NOT_CONFIGURED = 'The feature registry is not configured.';

interface UserSettingsTabProps {
  readonly user: User;
}

const UserSettingsTab = (props: UserSettingsTabProps) => {
  const { user } = props;
  const dispatch = useAppDispatch();
  const featureRegistry = useListFeatureFlagsQuery();
  const { grantsHeldAtLoad } = useAppSelector(userState);
  const granted = user.availableFeatures ?? [];
  const registryUnavailableMessage = featureRegistry.isError
    ? FEATURE_LIST_FAILED
    : featureRegistry.data?.configured === false
      ? FEATURE_REGISTRY_NOT_CONFIGURED
      : undefined;

  let recacheMetrics = {
    num10Sec: 0,
    num30Sec: 0,
    num1Min: 0,
    num10Min: 0,
    num1Hour: 0,
    num1Day: 0,
  };
  if (user) {
    recacheMetrics = {
      num10Sec: Math.ceil(10000 / user.delayBetweenURLs),
      num30Sec: Math.ceil(30000 / user.delayBetweenURLs),
      num1Min: Math.ceil(60000 / user.delayBetweenURLs),
      num10Min: Math.ceil(600000 / user.delayBetweenURLs),
      num1Hour: Math.ceil((60000 * 60) / user.delayBetweenURLs),
      num1Day: Math.ceil((60000 * 60 * 24) / user.delayBetweenURLs),
    };
  }

  const changeMobileAdaptive = (event: ChangeEvent) => {
    const checked = !user.mobileAdaptive;
    dispatch(setMobileAdaptive(checked));
  };

  const changeCustomRecacheEnabled = (event: ChangeEvent) => {
    const checked = user?.environment.customRecacheDelay === null;
    dispatch(setCustomRecacheDelay(checked ? user.delayBetweenURLs : null));
  };

  const changeCustomRecacheDelay = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setCustomRecacheDelay(parseInt(event.target.value, 10) || 1));
  };

  const changeUserPausedRecaching = (event: ChangeEvent) => {
    const checked = !user.userPausedRecaching;
    dispatch(setUserPausedRecaching(checked));
  };

  const changeIgnoreAllQueryParams = (event: ChangeEvent) => {
    const checked = !user.ignoreAllQueryParams;
    dispatch(setIgnoreAllQueryParams(checked));
  };

  const changeWhitelistQueryParams = (event: ChangeEvent) => {
    const checked = !user.whitelistQueryParams;
    dispatch(setWhitelistQueryParams(checked));
  };

  const changeExtractHiddenCss = (event: ChangeEvent) => {
    const checked = !user.extractHiddenCss;
    dispatch(setExtractHiddenCss(checked));
  };

  const changeOnlyServeCacheHits = (event: ChangeEvent) => {
    const checked = !user.onlyServeCacheHits;
    dispatch(setOnlyServeCacheHits(checked));
  };

  const changeForceApiAccess = (event: ChangeEvent) => {
    const checked = !user.settings.forceApiAccess;
    dispatch(setForceApiAccess(checked));
  };

  const changeCacheFreshness = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value) || 3;
    if (value < 0 || value > 365) {
      return;
    }
    dispatch(setCacheFreshness(value));
  };

  const changeExtractHiddenCssLocation = (event: ChangeEvent<HTMLSelectElement>) => {
    dispatch(setExtractHiddenCssLocation(event.target.value as ExtractHiddenCssLocation));
  };

  const changeSlowRenderTriggerSeconds = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10) || 0;

    if (value < 5) {
      return;
    }

    dispatch(setSlowRenderTriggerSeconds(value));
  };

  const toggleFeature = (key: string) => {
    const isRemovingMcp =
      key === MCP_FLAGS.MCP_ENABLED && granted.includes(MCP_FLAGS.MCP_ENABLED);
    const keysToRemove: readonly string[] = isRemovingMcp
      ? [MCP_FLAGS.MCP_ENABLED, ...MCP_DEPENDENT_FLAGS]
      : [key];
    const updated = granted.includes(key)
      ? granted.filter((f) => !keysToRemove.includes(f))
      : [...granted, key];
    dispatch(setAvailableFeatures(updated));
  };

  return (
    <Fragment>
      <div className="form-group">
        <label id="available-features" className="form-label fw-bold">
          Available Features
        </label>
        {registryUnavailableMessage && <p className="text-muted">{registryUnavailableMessage}</p>}
        {featureRegistry.isLoading && <p className="text-muted">{FEATURE_LIST_LOADING}</p>}
        <RequireTier requiredTier="superadmin" policy="disable">
          <fieldset aria-labelledby="available-features" className="available-features ms-2 border-0 p-0 m-0">
            {registryUnavailableMessage
              ? granted.map((key) => (
                  <div key={key} className="form-check form-switch">
                    <label htmlFor={`feature-${key}`} className="form-label">
                      {key}
                    </label>
                    <input type="checkbox" className="form-check-input" id={`feature-${key}`} checked disabled readOnly />
                  </div>
                ))
              : featureRegistry.data &&
                buildFeatureRows(featureRegistry.data.flags, grantsHeldAtLoad).map(
                  ({ key, label, registered, overridingState }) => (
                    <div key={key} className="form-check form-switch">
                      <label htmlFor={`feature-${key}`} className="form-label">
                        {label}
                      </label>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`feature-${key}`}
                        checked={granted.includes(key)}
                        disabled={isToggleDisabled(key, granted)}
                        aria-describedby={
                          !registered
                            ? `feature-${key}-not-registered`
                            : overridingState
                              ? `feature-${key}-overriding-state`
                              : undefined
                        }
                        onChange={() => toggleFeature(key)}
                      />
                      {!registered && (
                        <span id={`feature-${key}-not-registered`} className="badge text-bg-warning ms-2">
                          not registered
                        </span>
                      )}
                      {overridingState && (
                        <span id={`feature-${key}-overriding-state`} className="badge text-bg-secondary ms-2">
                          {overridingState}: grant has no effect
                        </span>
                      )}
                    </div>
                  ),
                )}
          </fieldset>
        </RequireTier>
      </div>

      <hr />

      <div className="form-group">
        <label htmlFor="cacheFreshness" className="form-label">
          cache freshness
        </label>
        <RequireTier requiredTier="admin" policy="disable" fullWidth>
          <input
            type="number"
            className="form-control"
            id="cacheFreshness"
            defaultValue={user.environment.cacheFreshness}
            onChange={changeCacheFreshness}
          />
        </RequireTier>
      </div>

      <div className="form-group">
        <label htmlFor="slowRenderTriggerSeconds" className="form-label">
          slow render trigger seconds (min 5, default 21)
        </label>
        <RequireTier requiredTier="admin" policy="disable" fullWidth>
          <input
            type="number"
            className="form-control"
            id="slowRenderTriggerSeconds"
            defaultValue={user.environment.slowRenderTriggerSeconds}
            onChange={changeSlowRenderTriggerSeconds}
            min="5"
          />
        </RequireTier>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="customRecacheEnabled" className="form-label">
                custom recache delay
              </label>
              <RequireTier requiredTier="admin" policy="disable">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="customRecacheEnabled"
                  checked={user.environment.customRecacheDelay !== null}
                  onChange={changeCustomRecacheEnabled}
                />
              </RequireTier>
            </div>
          </div>
        </div>
        {user.environment.customRecacheDelay !== null && (
          <div className="col">
            <div className="form-group">
              <label htmlFor="customRecacheDelay" className="form-label">
                custom recache delay
              </label>
              <RequireTier requiredTier="admin" policy="disable" fullWidth>
                <input
                  type="number"
                  className="form-control"
                  id="customRecacheDelay"
                  value={user.environment.customRecacheDelay}
                  onChange={changeCustomRecacheDelay}
                />
              </RequireTier>
            </div>
          </div>
        )}
        <div className="col">
          <table className="table table-sm table-nowrap">
            <caption className="caption-top">Recache Metrics</caption>
            <thead>
              <tr>
                <th scope="col">in 10 sec</th>
                <th scope="col">in 30 sec</th>
                <th scope="col">in 1 min</th>
                <th scope="col">in 10 min</th>
                <th scope="col">in 1 hour</th>
                <th scope="col">in 1 day</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{recacheMetrics.num10Sec}</td>
                <td>{recacheMetrics.num30Sec}</td>
                <td>{recacheMetrics.num1Min}</td>
                <td>{recacheMetrics.num10Min}</td>
                <td>{recacheMetrics.num1Hour}</td>
                <td>{recacheMetrics.num1Day}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <UserSettings user={user} />

      <div className="form-group">
        <div className="form-check form-switch">
          <label htmlFor="mobileAdaptive" className="form-label">
            mobile adaptive
          </label>
          <input
            type="checkbox"
            className="form-check-input"
            id="mobileAdaptive"
            checked={user.mobileAdaptive}
            onChange={changeMobileAdaptive}
          />
        </div>
      </div>

      <div className="form-group">
        <div className="form-check form-switch">
          <label htmlFor="userPausedRecaching" className="form-label">
            user paused recaching
          </label>
          <input
            type="checkbox"
            className="form-check-input"
            id="userPausedRecaching"
            checked={user.userPausedRecaching}
            onChange={changeUserPausedRecaching}
          />
        </div>
      </div>

      <div className="form-group">
        <div className="form-check form-switch">
          <label htmlFor="whitelistQueryParams" className="form-label">
            Whitelist query params
          </label>
          <input
            type="checkbox"
            className="form-check-input"
            id="whitelistQueryParams"
            checked={user.whitelistQueryParams}
            onChange={changeWhitelistQueryParams}
          />
        </div>
      </div>

      <div className="form-group">
        <div className="form-check form-switch">
          <label htmlFor="ignoreAllQueryParams" className="form-label">
            Ignore all query parameters
          </label>
          <input
            type="checkbox"
            className="form-check-input"
            id="ignoreAllQueryParams"
            checked={user.ignoreAllQueryParams}
            onChange={changeIgnoreAllQueryParams}
          />
        </div>
      </div>

      <div className="form-group">
        <div className="form-check form-switch">
          <label htmlFor="extractHiddenCss" className="form-label">
            Extract hidden css
          </label>
          <input
            type="checkbox"
            className="form-check-input"
            id="extractHiddenCss"
            checked={user.extractHiddenCss}
            onChange={changeExtractHiddenCss}
          />
        </div>
      </div>
      {user.extractHiddenCss && (
        <div className="row">
          <div className="col">
            <label htmlFor="extractHiddenCssLocation" className="form-label">
              Extract hidden css location
            </label>
          </div>
          <div className="col">
            <select
              className="form-select"
              defaultValue={user.settings?.extractHiddenCssLocation || 'bottom'}
              onChange={changeExtractHiddenCssLocation}
            >
              <option value="top">top</option>
              <option value="bottom">bottom</option>
            </select>
          </div>
        </div>
      )}
      <div className="form-group">
        <div className="form-check form-switch">
          <label htmlFor="onlyServeCacheHits" className="form-label">
            Only serve cache hits
          </label>
          <input
            type="checkbox"
            className="form-check-input"
            id="onlyServeCacheHits"
            checked={user.onlyServeCacheHits}
            onChange={changeOnlyServeCacheHits}
          />
        </div>
      </div>
      <div className="form-group">
        <div className="form-check form-switch">
          <label htmlFor="forceApiAccess" className="form-label">
            Force API access
          </label>
          <input
            type="checkbox"
            className="form-check-input"
            id="forceApiAccess"
            checked={user.settings.forceApiAccess}
            onChange={changeForceApiAccess}
          />
        </div>
      </div>
      <UserUpdateButton />
    </Fragment>
  );
};

export default UserSettingsTab;
